<template>
  <div>
    <b-card title="Model 238">
      <p class="mb-2">
        Declaració informativa per a la comunicació d'informació per part d'operadors de plataformes.
        <a href="https://sede.agenciatributaria.gob.es/Sede/ca_es/procedimientoini/GI52.shtml" target="_blank">
          Més informació
        </a>
      </p>

      <!-- SELECT DATES -->
      <b-row class="my-3">
        <b-col cols="12">
          <h4>1. Selecciona les reserves a declarar</h4>
        </b-col>
        <b-col cols="12" class="mb-50 d-flex">
          <anual-selector v-model="reportDatesRange" />
          <b-button variant="primary" class="ml-50" :disabled="!canSearch" @click="fetchUnsentAeat238Reports">
            Cerca
          </b-button>
        </b-col>
      </b-row>

      <!-- VERIFY BOOKINGS -->
      <b-row class="my-3">
        <b-col cols="12">
          <h4>2. Verifica les reserves</h4>
        </b-col>
        <b-col cols="12">
          <b-overlay :show="loading" rounded="sm" variant="white" opacity="0.9">
            <b-alert v-if="!reportsByOwnerAndQuarter.length" variant="warning" :show="true">
              <div class="alert-body">
                Sense resultats. Selecciona les dates i clica al botó "Cerca"
              </div>
            </b-alert>

            <template v-else>
              <reports-table :items="reportsByOwnerAndQuarter" :loading="loading" />
              <!-- <b-button class="mr-50" @click="generateReport" disabled>
                Descarrega
              </b-button> -->
            </template>
          </b-overlay>
        </b-col>
      </b-row>

      <!-- UPLOAD XML TO AEAT -->
      <!-- <b-row class="my-3">
        <b-col cols="12">
          <h4>3. Carrega el fitxer XML a la AEAT</h4>
        </b-col>
        <b-col cols="12">
          <b-button class="mr-50"
            href="https://www1.agenciatributaria.gob.es/static_files/common/internet/dep/explotacion/ws/ClienteWSAEATModelos.html"
            target="_blank">
            Carrega
            <feather-icon icon="LinkIcon" class="ml-25" />
          </b-button>
        </b-col>
      </b-row> -->

      <!-- ANALIZE AEAT REPSONSE -->
      <!-- <b-row class="my-3">
        <b-col cols="12">
          <h4>4. Analitza el fitxer de resposta</h4>
        </b-col>
      </b-row> -->
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BAlert,
  BOverlay,
} from "bootstrap-vue";
import AnualSelector from "@/views/administration/aeat-reports/238/components/AnualSelector.vue";
import ReportsTable from "@/views/administration/aeat-reports/238/components/ReportsTable.vue";
import { formatDateStringToAeatDate } from "@/utils/formatters";
import { notifyError } from "@/utils/methods";
import { saveAs } from "file-saver";
import {
  AGENCY_LEGAL_NAME,
  AGENCY_CIF,
  AGENCY_CONTACT_NAME,
  AGENCY_PHONE,
} from "@appConfig";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BButton,
    BAlert,
    AnualSelector,
    ReportsTable
  },
  data() {
    return {
      aeatModelNumber: "238",
      aeatModelType: "A0",
      aeatModelVersion: "1.0",
      selectedClient: null,
      reportDatesRange: {
        from: null,
        to: null,
      },
    };
  },
  computed: {
    loading() {
      return this.$store.getters["bookings/loading"];
    },
    aeat238Bookings() {
      return this.$store.getters["bookings/unsentAeat238Reports"];
    },
    reportsByOwner() {
      // Group the aeat238 bookings by booking.contract.owner.idNumber
      const reports = this.aeat238Bookings.reduce((acc, booking) => {
        const owner = booking.contract.owner;
        const idNumber = owner.idNumber;
        if (!acc[idNumber]) {
          acc[idNumber] = {
            owner,
            bookings: [],
          };
        }
        acc[idNumber].bookings.push(booking);
        return acc;
      }, {});
      return Object.values(reports);
    },
    reportsByOwnerAndQuarter() {
      // For each reportsByOnwer.bookings, group them by quarter
      const reports = this.reportsByOwner.map((report) => {
        const quarters = {
          1: {
            bookings: [],
            totalRatesPrice: 0,
            totalOwnerRatesPrice: 0,
            totalProfitPrice: 0,
            totalCommissionPrice: 0,
            totalCommissionVatPrice: 0
          },
          2: {
            bookings: [],
            totalRatesPrice: 0,
            totalOwnerRatesPrice: 0,
            totalProfitPrice: 0,
            totalCommissionPrice: 0,
            totalCommissionVatPrice: 0
          },
          3: {
            bookings: [],
            totalRatesPrice: 0,
            totalOwnerRatesPrice: 0,
            totalProfitPrice: 0,
            totalCommissionPrice: 0,
            totalCommissionVatPrice: 0
          },
          4: {
            bookings: [],
            totalRatesPrice: 0,
            totalOwnerRatesPrice: 0,
            totalProfitPrice: 0,
            totalCommissionPrice: 0,
            totalCommissionVatPrice: 0
          }
        }

        report.bookings.forEach(booking => {
          const quarter = this.calculateQuarter(booking.checkin)
          quarters[quarter].bookings.push(booking);
          const pvpPrice = booking.ratesPrice || booking.pvpPrice;
          quarters[quarter].totalRatesPrice += pvpPrice;
          const ownerPrice = booking.ownerRatesPrice || booking.ownerPrice;
          quarters[quarter].totalOwnerRatesPrice += ownerPrice;
          quarters[quarter].totalProfitPrice += booking.profitPrice;
          const agencyPrice = pvpPrice - ownerPrice - booking.discountPrice;
          quarters[quarter].totalCommissionPrice += agencyPrice;
          quarters[quarter].totalCommissionVatPrice += (agencyPrice - booking.profitPrice);
        });

        return {
          owner: report.owner,
          quarters,
        };
      });

      return reports;
    },
    aeat238XmlText() {
      const aeat238CabeceraText = () => {
        return `<dec1:Cabecera>
          <dec1:TipoComunicacion>${this.aeatModelType}</dec1:TipoComunicacion>
          <dec1:Modelo>${this.aeatModelNumber}</dec1:Modelo>
          <dec1:Periodo>
            <dec1:Ejercicio>${this.selectedYear}</dec1:Ejercicio>
          </dec1:Periodo>
          <dec1:IDVersionModelo>${this.aeatModelVersion}</dec1:IDVersionModelo>
          <dec1:IDDeclarante>
            <dec1:NIF>${AGENCY_CIF}</dec1:NIF>
            <dec1:NombreRazon>${AGENCY_LEGAL_NAME}</dec1:NombreRazon>
            <dec1:PersonaContacto>
              <dec1:Telefono>${AGENCY_PHONE}</dec1:Telefono>
              <dec1:ApellidosNombre>${AGENCY_CONTACT_NAME}</dec1:ApellidosNombre>
            </dec1:PersonaContacto>
          </dec1:IDDeclarante>
        </dec1:Cabecera>`;
      };

      const aeat238DeclaradosText = () => {
        const text = [];
        this.reports.forEach((booking) => {
          const declaradoText = aeat238DeclaradoText(booking);
          if (declaradoText) text.push(declaradoText);
        });
        return text.length ? text.join("") : "¡¡¡SENSE DECLARADOS!!!";
      };

      const aeat238OwnerIdText = (owner) => {
        if (!owner.idType) return "¡¡¡SENSE TIPUS DE DOCUMENT!!!";
        if (!owner.idNumber) return "¡¡¡SENSE NUMERO DE DOCUMENT!!!";
        switch (owner.idType) {
          case "DNI":
          case "NIF":
          case "CIF":
            return `<dec1:NIF>${owner.idNumber || ""}</dec1:NIF>`;
          default:
            return `<dec1:IDOtro><dec1:CodigoPais>${owner.nationality || ""
              }</dec1:CodigoPais><dec1:IDType>06</dec1:IDType><dec1:ID>${owner.idNumber || ""
              }</dec1:ID></dec1:IDOtro>`;
        }
      };

      const aeat238OwnerText = (owner) => {
        if (!owner) return "¡¡¡SENSE PROPIETARI!!!";
        return `<dec1:IDDeclarado>
          <dec1:Clave>T</dec1:Clave>
          <dec1:NombreRazon>${owner.fullName || ""}</dec1:NombreRazon>
          ${aeat238OwnerIdText(owner)}
        </dec1:IDDeclarado>`;
      };

      const aeat238IDDeclaradosText = (booking) => {
        const contract = booking?.contract || null;
        if (!contract) return "¡¡¡SENSE CONTRACTE!!!";

        const text = [];

        const owner = contract.owner;
        if (owner) {
          text.push(aeat238OwnerText(owner));
        }

        const coOwners = contract.coOwners;
        if (coOwners.length) {
          coOwners.forEach((coOwner) => text.push(aeat238OwnerText(coOwner)));
        }

        return text.length ? text.join("") : "¡¡¡SENSE PROPIETARIS!!!";
      };

      const getAddressTypeName = (code) => {
        if (!code) return null;
        switch (code) {
          case "STREET":
            return "CALLE";
          case "AVENUE":
            return "AVDA";
          case "PATH":
          case "PATHWAY":
            return "CAMIN";
          case "ROAD":
            return "CARRE";
          case "DISSEMINATED":
            return "DISEM";
          case "WAY":
            return "VIA";
          case "FREEWAY":
            return "AUTOV";
          case "SQUARE":
            return "PLAZA";
          case "COURT":
            return "PATIO";
          case "PASSAGE":
            return "PASAG";
          case "PROMENADE":
            return "PASEO";
          case "RAMBLA":
            return "RAMBL";
          case "OTHER":
          default:
            return "OTR";
        }
      };

      const getAddressName = (location) => {
        if (!location) return null;
        switch (location.addressType) {
          case "STREET":
          case "AVENUE":
          case "PATH":
          case "PATHWAY":
          case "ROAD":
          case "WAY":
          case "FREEWAY":
          case "SQUARE":
          case "COURT":
          case "PASSAGE":
          case "PROMENADE":
          case "RAMBLA":
            return location.addressName?.trim() ?? "";
          case "DISSEMINATED":
            return `Polígono ${location.addressPolygon} Parcela ${location.addressPlot}`;
          case "OTHER":
          default:
            return "";
        }
      };

      const getIdTypeCode = (nationality) => {
        if (!nationality || nationality !== "ES") return "06";
        return "03";
      };

      const aeat238DeclaradoText = (booking) => {
        if (!booking) return null;

        const localizator = booking.localizator || "";
        const client = booking.client || null;
        const clientName = client?.fullName?.trim() ?? "";
        const clientIdNumber = client?.idNumber?.trim() ?? "";
        const clientLocation = client?.location || null;
        const clientNationality =
          client?.nationality ?? (clientLocation?.country || "");
        const accommodation = booking.accommodation || null;
        const cadastralReference =
          accommodation?.cadastralReference?.trim() ?? "";
        const location = accommodation?.location || null;
        const zip = location?.zip?.trim() || null;
        const provinceCode = zip?.substring(0, 2) || "";
        const cityCode = zip?.substring(2) || "";
        const addressType = getAddressTypeName(location?.addressType) || "";
        const addressName = getAddressName(location) ?? "";
        const addressNumberType = location?.addressNumber ? "NUM" : "S/N";
        const addressNumber = location?.addressNumber?.trim() ?? "";
        const checkin = formatDateStringToAeatDate(booking.checkin) || "";
        const nights = booking.nights || "";
        const price = (booking.ratesPrice / 100).toFixed(2);

        return `<dec1:Declarado>
          <dec1:IDRegistroDeclarado>${localizator}</dec1:IDRegistroDeclarado>
          ${aeat238IDDeclaradosText(booking)}
          <dec1:Detalle>
            <dec1:IDCesionario>
              <dec1:NombreRazon>${clientName}</dec1:NombreRazon>
              <dec1:IDOtro>
                <dec1:CodigoPais>${clientNationality}</dec1:CodigoPais>
                <dec1:IDType>${getIdTypeCode(clientNationality)}</dec1:IDType>
                <dec1:ID>${clientIdNumber}</dec1:ID>
              </dec1:IDOtro>
            </dec1:IDCesionario>
            <dec1:IDInmueble>
              <dec1:SituacionInmueble>01</dec1:SituacionInmueble>
              <dec1:DatosInmueble>
                <dec1:ReferenciaCatastral>${cadastralReference}</dec1:ReferenciaCatastral>
                <dec1:Direccion>
                  <dec1:CodigoMunicipio>${cityCode}</dec1:CodigoMunicipio>
                  <dec1:CodigoProvincia>${provinceCode}</dec1:CodigoProvincia>
                  <dec1:TipoVia>${addressType}</dec1:TipoVia>
                  <dec1:NombreVia>${addressName}</dec1:NombreVia>
                  <dec1:TipoNumeracion>${addressNumberType}</dec1:TipoNumeracion>
                  <dec1:NumeroVivienda>${addressNumber}</dec1:NumeroVivienda>
                </dec1:Direccion>
              </dec1:DatosInmueble>
            </dec1:IDInmueble>
            <dec1:DesgloseOperacion>
              <dec1:PeriodoCesion>
                <dec1:FechaInicioCesion>${checkin}</dec1:FechaInicioCesion>
                <dec1:NumeroDiasDisfrute>${nights}</dec1:NumeroDiasDisfrute>
              </dec1:PeriodoCesion>
              <dec1:Importe>${price}</dec1:Importe>
            </dec1:DesgloseOperacion>
          </dec1:Detalle>
        </dec1:Declarado>`;
      };

      return `<?xml version="1.0" encoding="UTF-8"?>
      <soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:dec="https://www2.agenciatributaria.gob.es/static_files/common/internet/dep/aplicaciones/es/aeat/ddii/enol/ws/Declaracion.xsd" xmlns:dec1="https://www2.agenciatributaria.gob.es/static_files/common/internet/dep/aplicaciones/es/aeat/ddii/enol/ws/DeclaracionInformativa.xsd">
        <soapenv:Header/>
        <soapenv:Body>
          <dec:Declaracion>
            ${aeat238CabeceraText()}
            ${aeat238DeclaradosText()}
          </dec:Declaracion>
        </soapenv:Body>
      </soapenv:Envelope>`;
    },
    selectedYear() {
      const splittedSelectedFromDate = this.reportDatesRange.from.split("-");
      return splittedSelectedFromDate[0];
    },
    canSearch() {
      return (
        !this.loading &&
        !!this.reportDatesRange.from &&
        !!this.reportDatesRange.to
      );
    },
  },
  methods: {
    fetchUnsentAeat238Reports() {
      return this.$store
        .dispatch("bookings/fetchBookingsWithUnsentAeat238Report", {
          startDate: this.reportDatesRange.from,
          endDate: this.reportDatesRange.to,
        })
        .catch(() => {
          notifyError(
            this.$t("errors.fetchBookings.title"),
            this.$t("errors.fetchBookings.description")
          );
        });
    },
    generateReport() {
      const blob = new Blob([this.aeat238XmlText], {
        type: "text/plain;charset=utf-8",
      });
      const fileName = `XML238.${this.selectedYear}.xml`;
      saveAs(blob, fileName);
    },
    calculateQuarter(date) {
      // date example: "2025-02-01T00:00:00+00:00"
      const parsedDate = new Date(date);

      // Guard against invalid dates
      if (Number.isNaN(parsedDate.getTime())) return null; // or throw new Error("Invalid date")

      const month = parsedDate.getMonth() + 1; // 1..12
      return Math.floor((month - 1) / 3) + 1;  // 1..4
    }
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
